// src/services/offlineDb.ts
// IndexedDB wrapper for offline-first data persistence.
// Provides typed stores for every domain, sync queue management, and pending file storage.

const DB_NAME = 'menuhouse-offline';
const DB_VERSION = 2;

// ── Types ──

export interface SyncOperation {
  id?: number;                    // auto-increment
  timestamp: number;              // when queued
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;                    // API endpoint
  data?: any;                     // request body
  headers?: Record<string, string>;
  entityType: string;             // 'dish', 'category', etc.
  entityId?: string;              // for optimistic updates
  tempId?: string;                // client-generated temp ID
  hasFiles?: boolean;             // whether it includes file uploads
  fileRefs?: string[];            // keys in pendingFiles store
  status: 'pending' | 'syncing' | 'completed' | 'failed';
  retryCount: number;
  lastError?: string;
}

export interface PendingFile {
  id?: number;
  ref: string;        // unique reference key
  blob: Blob;
  filename: string;
  mimeType: string;
  fieldName: string;  // form field name (e.g. 'image', 'logo')
  timestamp: number;
}

export interface SyncMeta {
  key: string;
  lastSync: number;
}

// All available store names
export type StoreName =
  | 'categories'
  | 'dishes'
  | 'orders'
  | 'tables'
  | 'staff'
  | 'materials'
  | 'inventoryCategories'
  | 'recipeTemplates'
  | 'expenses'
  | 'cashRegisters'
  | 'shiftReports'
  | 'restaurants'
  | 'restaurantProfile'
  | 'publicMenus'
  | 'dashboardStats'
  | 'syncQueue'
  | 'syncMeta'
  | 'pendingFiles'
  | 'deliveryCompanies'
  | 'deliveryCompanyOrders';

// ── Database Setup ──

let dbInstance: IDBDatabase | null = null;
let dbPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Data stores — keyed by 'id' (or 'slug'/'key' for special ones)
      const dataStores: Array<{ name: string; keyPath: string }> = [
        { name: 'categories', keyPath: 'id' },
        { name: 'dishes', keyPath: 'id' },
        { name: 'orders', keyPath: 'id' },
        { name: 'tables', keyPath: 'id' },
        { name: 'staff', keyPath: 'id' },
        { name: 'materials', keyPath: 'id' },
        { name: 'inventoryCategories', keyPath: 'id' },
        { name: 'recipeTemplates', keyPath: 'id' },
        { name: 'expenses', keyPath: 'id' },
        { name: 'cashRegisters', keyPath: 'id' },
        { name: 'shiftReports', keyPath: 'id' },
        { name: 'restaurants', keyPath: 'id' },
        { name: 'restaurantProfile', keyPath: 'id' },
        { name: 'publicMenus', keyPath: 'slug' },
        { name: 'dashboardStats', keyPath: 'key' },
        { name: 'syncMeta', keyPath: 'key' },
        { name: 'deliveryCompanies', keyPath: 'id' },
        { name: 'deliveryCompanyOrders', keyPath: 'id' },
      ];

      for (const store of dataStores) {
        if (!db.objectStoreNames.contains(store.name)) {
          db.createObjectStore(store.name, { keyPath: store.keyPath });
        }
      }

      // Sync queue — auto-increment key
      if (!db.objectStoreNames.contains('syncQueue')) {
        const sqStore = db.createObjectStore('syncQueue', {
          keyPath: 'id',
          autoIncrement: true,
        });
        sqStore.createIndex('status', 'status', { unique: false });
        sqStore.createIndex('entityType', 'entityType', { unique: false });
        sqStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Pending files — auto-increment key
      if (!db.objectStoreNames.contains('pendingFiles')) {
        const pfStore = db.createObjectStore('pendingFiles', {
          keyPath: 'id',
          autoIncrement: true,
        });
        pfStore.createIndex('ref', 'ref', { unique: true });
      }
    };

    request.onsuccess = (event) => {
      dbInstance = (event.target as IDBOpenDBRequest).result;

      // Handle unexpected close (e.g. versioning upgrade in another tab)
      dbInstance.onclose = () => {
        dbInstance = null;
        dbPromise = null;
      };

      resolve(dbInstance);
    };

    request.onerror = (event) => {
      dbPromise = null;
      console.error('[OfflineDB] Failed to open database:', (event.target as IDBOpenDBRequest).error);
      reject((event.target as IDBOpenDBRequest).error);
    };
  });

  return dbPromise;
}

// ── Generic CRUD helpers ──

function txn(
  storeName: string,
  mode: IDBTransactionMode
): Promise<{ store: IDBObjectStore; tx: IDBTransaction }> {
  return openDatabase().then((db) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    return { store, tx };
  });
}

function wrapRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ── Public API ──

export const db = {
  // ─── Generic CRUD ───

  async put<T>(storeName: StoreName, data: T): Promise<void> {
    const { store } = await txn(storeName, 'readwrite');
    await wrapRequest(store.put(data));
  },

  async putMany<T>(storeName: StoreName, items: T[]): Promise<void> {
    const { store } = await txn(storeName, 'readwrite');
    for (const item of items) {
      store.put(item);
    }
    // The transaction auto-commits when all requests succeed
  },

  async get<T>(storeName: StoreName, key: IDBValidKey): Promise<T | undefined> {
    const { store } = await txn(storeName, 'readonly');
    return wrapRequest<T | undefined>(store.get(key) as IDBRequest<T | undefined>);
  },

  async getAll<T>(storeName: StoreName): Promise<T[]> {
    const { store } = await txn(storeName, 'readonly');
    return wrapRequest<T[]>(store.getAll() as IDBRequest<T[]>);
  },

  async delete(storeName: StoreName, key: IDBValidKey): Promise<void> {
    const { store } = await txn(storeName, 'readwrite');
    await wrapRequest(store.delete(key));
  },

  async clear(storeName: StoreName): Promise<void> {
    const { store } = await txn(storeName, 'readwrite');
    await wrapRequest(store.clear());
  },

  async count(storeName: StoreName): Promise<number> {
    const { store } = await txn(storeName, 'readonly');
    return wrapRequest(store.count());
  },

  // ─── Sync Queue Operations ───

  async enqueue(operation: Omit<SyncOperation, 'id'>): Promise<number> {
    const { store } = await txn('syncQueue', 'readwrite');
    const id = await wrapRequest<IDBValidKey>(store.add(operation));
    return id as number;
  },

  async dequeue(): Promise<SyncOperation | null> {
    const { store } = await txn('syncQueue', 'readwrite');
    const index = store.index('status');
    const request = index.openCursor(IDBKeyRange.only('pending'));
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const op = cursor.value as SyncOperation;
          // Mark as syncing
          op.status = 'syncing';
          cursor.update(op);
          resolve(op);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  },

  async peekAll(): Promise<SyncOperation[]> {
    return this.getAll<SyncOperation>('syncQueue');
  },

  async getPending(): Promise<SyncOperation[]> {
    const { store } = await txn('syncQueue', 'readonly');
    const index = store.index('status');
    return wrapRequest<SyncOperation[]>(index.getAll('pending') as IDBRequest<SyncOperation[]>);
  },

  async getFailed(): Promise<SyncOperation[]> {
    const { store } = await txn('syncQueue', 'readonly');
    const index = store.index('status');
    return wrapRequest<SyncOperation[]>(index.getAll('failed') as IDBRequest<SyncOperation[]>);
  },

  async markCompleted(id: number): Promise<void> {
    const op = await this.get<SyncOperation>('syncQueue', id);
    if (op) {
      op.status = 'completed';
      await this.put('syncQueue', op);
    }
  },

  async markFailed(id: number, error: string): Promise<void> {
    const op = await this.get<SyncOperation>('syncQueue', id);
    if (op) {
      op.status = 'failed';
      op.lastError = error;
      op.retryCount = (op.retryCount || 0) + 1;
      await this.put('syncQueue', op);
    }
  },

  async resetFailed(): Promise<void> {
    const failed = await this.getFailed();
    for (const op of failed) {
      op.status = 'pending';
      op.lastError = undefined;
      await this.put('syncQueue', op);
    }
  },

  async removeCompleted(): Promise<void> {
    const all = await this.peekAll();
    for (const op of all) {
      if (op.status === 'completed' && op.id != null) {
        await this.delete('syncQueue', op.id);
      }
    }
  },

  async pendingCount(): Promise<number> {
    const { store } = await txn('syncQueue', 'readonly');
    const index = store.index('status');
    return wrapRequest(index.count('pending'));
  },

  async failedCount(): Promise<number> {
    const { store } = await txn('syncQueue', 'readonly');
    const index = store.index('status');
    return wrapRequest(index.count('failed'));
  },

  // ─── Pending Files ───

  async addFile(file: Omit<PendingFile, 'id'>): Promise<number> {
    const { store } = await txn('pendingFiles', 'readwrite');
    const id = await wrapRequest<IDBValidKey>(store.add(file));
    return id as number;
  },

  async getFileByRef(ref: string): Promise<PendingFile | undefined> {
    const { store } = await txn('pendingFiles', 'readonly');
    const index = store.index('ref');
    return wrapRequest<PendingFile | undefined>(index.get(ref) as IDBRequest<PendingFile | undefined>);
  },

  async removeFile(ref: string): Promise<void> {
    const file = await this.getFileByRef(ref);
    if (file?.id != null) {
      await this.delete('pendingFiles', file.id);
    }
  },

  // ─── Sync Meta ───

  async getLastSync(entityType: string): Promise<number | null> {
    const meta = await this.get<SyncMeta>('syncMeta', entityType);
    return meta?.lastSync ?? null;
  },

  async setLastSync(entityType: string): Promise<void> {
    await this.put('syncMeta', { key: entityType, lastSync: Date.now() });
  },

  // ─── Offline Order Counter ───

  async getNextOfflineOrderNumber(): Promise<string> {
    const counterKey = 'offlineOrderCounter';
    const meta = await this.get<{ key: string; value: number }>('syncMeta', counterKey);
    const nextVal = (meta?.value ?? 0) + 1;
    await this.put('syncMeta', { key: counterKey, value: nextVal });
    return `OFF-${String(nextVal).padStart(4, '0')}`;
  },

  // ─── Utility ───

  /** Get pending sync operations by entity type (e.g. for merging offline orders) */
  async getByEntityType(entityType: string): Promise<SyncOperation[]> {
    const { store } = await txn('syncQueue', 'readonly');
    const index = store.index('entityType');
    return wrapRequest<SyncOperation[]>(index.getAll(entityType) as IDBRequest<SyncOperation[]>);
  },

  /** Generate a unique temp ID for offline-created entities */
  generateTempId(prefix: string = 'temp'): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },
};

export default db;
